import { z } from 'zod'
import { UTCMillisecondsSchema } from '../time'
import { CompanyIdSchema, UserIdSchema, type CompanyId } from '../ids'
import { TeamLevelSchema } from '../entity'
import { AuditPropsSchema, UPLOAD_URL_DESCRIPTION } from '../common'
import { ArchivableSchema } from '../archive'

export const ALL_COMPANIES: CompanyId = 'C:*'
export const TANTALIM_ID: CompanyId = 'C:tantalim'
export const GLOBALTEMPLATE_ID: CompanyId = 'C.template1'
export const SUNRUN_ID: CompanyId = 'C:7df4ea7b'
export const AMBIA_ID: CompanyId = 'C:4e858521'
export const SUNRUN_TEST_ID: CompanyId = 'C:bfc8a009'
export const SEP_ID: CompanyId = 'C:f16652c0'
export const FAMILY_HERITAGE_ID: CompanyId = 'C.HIakJZix'
export const ESP_ROOFING_ID: CompanyId = 'C.5hUOYdy9'
export const DEMO_DEALER_ID: CompanyId = 'C.AZQP6JTp'

export const WithCompanyIdSchema = z.object({
  companyId: CompanyIdSchema,
})
export type WithCompanyId = z.infer<typeof WithCompanyIdSchema>

/** used in unit tests */
export function stubCompanyId(id?: string): CompanyId {
  return `C:${id ?? 'ID'}`
}

export const toCompanyId = (company: WithCompanyId): CompanyId => company.companyId

/** used in unit tests */
export function stubUnsavedCompany(data?: Partial<UnsavedCompany>): UnsavedCompany {
  return {
    name: 'Stub Company',
    ...data,
  }
}

export const COMPANY_KINDS = ['customer', 'sandbox', 'demo', 'template', 'development'] as const
export const CompanyKindSchema = z.enum(COMPANY_KINDS).meta({ id: 'companyKind' })
export type CompanyKind = z.infer<typeof CompanyKindSchema>

export const ACCESS_STATUSES = ['active', 'suspended'] as const
export const AccessStatusSchema = z.enum(ACCESS_STATUSES).meta({ id: 'accessStatus' })
export type AccessStatus = z.infer<typeof AccessStatusSchema>

export const BILLING_METHODS = ['stripe', 'none'] as const
export const BillingMethodSchema = z.enum(BILLING_METHODS).meta({ id: 'billingMethod' })
export type BillingMethod = z.infer<typeof BillingMethodSchema>

export const COMPANY_INDUSTRIES = [
  'solar',
  'pest',
  'alarm',
  'roofing',
  'fiber',
  'insurance',
  'home_improvement',
  'windows',
  'satellite_tv',
  'retail',
  'other',
  'solar_cleaning',
] as const
export const CompanyIndustrySchema = z.enum(COMPANY_INDUSTRIES).meta({ id: 'companyIndustry' })
export type CompanyIndustry = z.infer<typeof CompanyIndustrySchema>

export const CompanySizeSchema = z.number().int().positive().meta({ id: 'companySize' })
export type CompanySize = z.infer<typeof CompanySizeSchema>

export const ManualAccessOverrideSchema = z.object({
  ...AuditPropsSchema.shape,
  accessStatus: AccessStatusSchema,
  note: z.string().optional(),
  expiresAt: UTCMillisecondsSchema,
})
export type ManualAccessOverride = z.infer<typeof ManualAccessOverrideSchema>

export const UnsavedCompanySchema = z.object({
  name: z.string().nonempty().meta({
    id: 'companyName',
    description: "Company's name",
    example: 'Acme Corporation',
  }),
  legalName: z.string().optional(),
  kind: CompanyKindSchema.optional(),
  accessStatus: AccessStatusSchema.optional(),
  internalOwnerId: UserIdSchema.optional(),
  billingEmail: z.string().optional(),
  billingMethod: BillingMethodSchema.optional(),
  maxUsers: z.number().int().optional(),
  parentCompanyId: CompanyIdSchema.optional(),
  salesforceAccountId: z.string().optional().meta({ description: 'Salesforce account ID linked to this company' }),
  size: CompanySizeSchema.optional(),
  industries: z.array(CompanyIndustrySchema).optional(),
  salesChannels: z.array(z.string()).optional(),
  demoOwnerId: UserIdSchema.optional().meta({ deprecated: true }),
})
export type UnsavedCompany = z.infer<typeof UnsavedCompanySchema>

export const CLOSER_MODELS = ['setter', 'dispatcher', 'system'] as const
export const CloserModelSchema = z.enum(CLOSER_MODELS)
export type CloserModel = z.infer<typeof CloserModelSchema>

export const SYSTEM_CLOSER_STRATEGY_TYPES = ['random', 'balanced', 'preferential', 'performance'] as const
export const SystemCloserStrategyTypeSchema = z.enum(SYSTEM_CLOSER_STRATEGY_TYPES)
export type SystemCloserStrategyType = z.infer<typeof SystemCloserStrategyTypeSchema>

const RandomStrategySchema = z.object({ type: z.literal('random') })
const BalancedStrategySchema = z.object({ type: z.literal('balanced'), windowDays: z.number().optional() })
const PreferentialStrategySchema = z.object({ type: z.literal('preferential'), jitter: z.number().min(0).max(100) })
const PerformanceStrategySchema = z.object({
  type: z.literal('performance'),
  kpi: z.string(),
  days: z.number().min(1),
  jitter: z.number().min(0).max(100),
})
export type PerformanceStrategy = z.infer<typeof PerformanceStrategySchema>

export const SystemCloserStrategySchema = z.discriminatedUnion('type', [
  RandomStrategySchema,
  BalancedStrategySchema,
  PreferentialStrategySchema,
  PerformanceStrategySchema,
])
export type SystemCloserStrategy = z.infer<typeof SystemCloserStrategySchema>

export const DEMO_PROGRESS_STATES = ['Ready', 'Copying', 'Failed'] as const
export const DemoStateSchema = z.enum(DEMO_PROGRESS_STATES).meta({ id: 'demoProgressState' })
export type DemoState = z.infer<typeof DemoStateSchema>

export const CompanyDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...UnsavedCompanySchema.required({ name: true, kind: true, accessStatus: true }).shape,
  ...ArchivableSchema.shape,
  companyId: CompanyIdSchema,
  accessOverride: ManualAccessOverrideSchema.optional(),
  highestTeamLevel: TeamLevelSchema.optional(),
  lowestTeamLevel: TeamLevelSchema.optional(),
  levelNames: z.record(z.string(), z.string()).optional(),
  timeZone: z.string().optional(),
  avatarUrl: z.string().optional().meta({ id: 'companyAvatarUrl' }),
  avatarBlurhash: z.string().optional().meta({ id: 'companyAvatarBlurhash' }),
  uploadAvatarUrl: z.string().optional().meta({ description: UPLOAD_URL_DESCRIPTION }),
  iconUrl: z.string().optional(),
  iconBlurhash: z.string().optional(),
  uploadIconUrl: z.string().optional().meta({ description: UPLOAD_URL_DESCRIPTION }),
  demoState: DemoStateSchema.optional(),
  activeUserCount: z.number().optional(),
})
export type CompanyData = z.infer<typeof CompanyDataSchema>

export const PartialCompanyDataSchema = z.object({
  ...CompanyDataSchema.partial().shape,
  legalName: CompanyDataSchema.shape.legalName.nullable().optional(),
  internalOwnerId: CompanyDataSchema.shape.internalOwnerId.nullable().optional(),
  salesforceAccountId: CompanyDataSchema.shape.salesforceAccountId.nullable().optional(),
  demoOwnerId: CompanyDataSchema.shape.demoOwnerId.nullable().optional(),
  ...WithCompanyIdSchema.shape,
})
export type PartialCompanyData = z.infer<typeof PartialCompanyDataSchema>

/** used in unit tests */
export function stubCompanyData(data?: Partial<CompanyData>): CompanyData {
  return {
    ...stubUnsavedCompany(),
    companyId: stubCompanyId(),
    kind: 'demo',
    accessStatus: 'active',
    ...data,
  }
}
export const TinyCompanySchema = CompanyDataSchema.pick({
  companyId: true,
  name: true,
  kind: true,
  accessStatus: true,
  industries: true,
  modifiedBy: true,
  demoState: true,
  avatarUrl: true,
  avatarBlurhash: true,
  maxUsers: true,
  activeUserCount: true,
  salesforceAccountId: true,
  internalOwnerId: true,
})
export type TinyCompany = z.infer<typeof TinyCompanySchema>

export function toTinyCompany(data: CompanyData): TinyCompany {
  return {
    companyId: data.companyId,
    name: data.name,
    kind: data.kind,
    accessStatus: data.accessStatus,
    industries: data.industries,
    modifiedBy: data.modifiedBy,
    demoState: data.demoState,
    avatarUrl: data.avatarUrl,
    avatarBlurhash: data.avatarBlurhash,
    maxUsers: data.maxUsers,
    activeUserCount: data.activeUserCount,
    salesforceAccountId: data.salesforceAccountId,
    internalOwnerId: data.internalOwnerId,
  }
}

function compareCompany(
  company: WithCompanyId | CompanyId | undefined,
  targetCompanyId: CompanyId | CompanyId[]
): boolean {
  if (Array.isArray(targetCompanyId)) {
    return targetCompanyId.some((id) => compareCompany(company, id))
  }
  if (typeof company === 'object') {
    return compareCompany(company.companyId, targetCompanyId)
  }
  return company === targetCompanyId
}

export function isFamilyHeritage(company?: WithCompanyId | CompanyId): boolean {
  return compareCompany(company, FAMILY_HERITAGE_ID)
}

export function isTantalim(company?: WithCompanyId | CompanyId): boolean {
  return compareCompany(company, TANTALIM_ID)
}

export function isSunrun(company?: WithCompanyId | CompanyId): boolean {
  return compareCompany(company, [SUNRUN_ID, SUNRUN_TEST_ID])
}
